import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { QueryParams, QueryResult } from 'src/app/modules/core/store/models/query.model';
import { TranslateService } from '@ngx-translate/core';
import { CommunicationsApiService } from '../store/communications/communications-api.service';
import {
  ChannelNotificationSettings,
  ChannelType,
  ChannelUIds,
  ChannelsCascadeSettings,
  CommunicationRecipientStatus,
  ConfiguredChannel,
  MassNotificationRecipient
} from '../store/communications';
import { catchError, map, take } from 'rxjs/operators';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { Permissions } from '@app/modules/authentication/store/auth';

@Injectable({
  providedIn: 'root'
})
export class ChannelSelectionService implements OnDestroy {
  public channelsLoadedEvents$: Subject<void> = new Subject();
  constructor(
    private translateService: TranslateService,
    private communicationsApiService: CommunicationsApiService,
    private userContext: UserContextService
  ) {}

  availableChannels: ConfiguredChannel[];
  loadingChannels: boolean;

  startTimes = [
    { value: 0, name: this.translateService.instant('COMMUNICATIONS.START-SEND-IMMEDIATE') },
    { value: 15, name: this.translateService.instant('COMMUNICATIONS.START-SEND-MINUTES', { value: 15 }) },
    { value: 30, name: this.translateService.instant('COMMUNICATIONS.START-SEND-MINUTES', { value: 30 }) },
    { value: 45, name: this.translateService.instant('COMMUNICATIONS.START-SEND-MINUTES', { value: 45 }) },
    { value: 60, name: this.translateService.instant('COMMUNICATIONS.START-SEND-HOURS', { value: 1 }) },
    { value: 90, name: this.translateService.instant('COMMUNICATIONS.START-SEND-HOURS', { value: 1.5 }) },
    { value: 120, name: this.translateService.instant('COMMUNICATIONS.START-SEND-HOURS', { value: 2 }) }
  ];

  resendTimes = [
    { value: 0, name: this.translateService.instant('COMMUNICATIONS.RESEND-ONCE') },
    { value: 5, name: this.translateService.instant('COMMUNICATIONS.RESEND-MINUTES', { value: 5 }) },
    { value: 10, name: this.translateService.instant('COMMUNICATIONS.RESEND-MINUTES', { value: 10 }) },
    { value: 15, name: this.translateService.instant('COMMUNICATIONS.RESEND-MINUTES', { value: 15 }) },
    { value: 30, name: this.translateService.instant('COMMUNICATIONS.RESEND-MINUTES', { value: 30 }) },
    { value: 45, name: this.translateService.instant('COMMUNICATIONS.RESEND-MINUTES', { value: 45 }) },
    { value: 60, name: this.translateService.instant('COMMUNICATIONS.RESEND-MINUTES', { value: 60 }) },
    { value: 120, name: this.translateService.instant('COMMUNICATIONS.RESEND-HOURS', { value: 2 }) },
    { value: 180, name: this.translateService.instant('COMMUNICATIONS.RESEND-HOURS', { value: 3 }) },
    { value: 360, name: this.translateService.instant('COMMUNICATIONS.RESEND-HOURS', { value: 6 }) },
    { value: 540, name: this.translateService.instant('COMMUNICATIONS.RESEND-HOURS', { value: 9 }) },
    { value: 720, name: this.translateService.instant('COMMUNICATIONS.RESEND-HOURS', { value: 12 }) },
    { value: 1440, name: this.translateService.instant('COMMUNICATIONS.RESEND-HOURS', { value: 24 }) }
  ];

  stopTimes = [
    { value: 15, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-MINUTES', { value: 15 }) },
    { value: 30, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-MINUTES', { value: 30 }) },
    { value: 45, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-MINUTES', { value: 45 }) },
    { value: 60, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 1 }) },
    { value: 90, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 1.5 }) },
    { value: 120, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 2 }) },
    { value: 150, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 2.5 }) },
    { value: 180, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 3 }) },
    { value: 210, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 3.5 }) },
    { value: 240, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 4 }) },
    { value: 720, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 12 }) },
    { value: 1440, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 24 }) },
    { value: 2880, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 48 }) },
    { value: 4320, name: this.translateService.instant('COMMUNICATIONS.STOP-SEND-HOURS', { value: 72 }) }
  ];

  public get hasChannels(): boolean {
    return !!this.availableChannels || this.loadingChannels === true;
  }

  public loadAvailableChannels(includeSelect: boolean): void {
    this.loadingChannels = true;
    this.getConfiguredChannels()
      .pipe(take(1))
      .subscribe(data => {
        this.availableChannels = [];
        const emptyChannel = {
          uId: null,
          channelUId: null,
          name: this.translateService.instant('COMMUNICATIONS.SELECT-CHANNEL')
        } as ConfiguredChannel;

        if (includeSelect) {
          this.availableChannels.push(emptyChannel);
        }

        if (data) {
          this.availableChannels.push(...data);
        }
        this.loadingChannels = false;
        this.channelsLoadedEvents$.next();
      });
  }

  public getUniqueChannelUIds(settings: ChannelsCascadeSettings): string[] {
    const channelUIds = [];

    settings?.channelNotifications?.forEach(c => {
      const channel = this.availableChannels?.find(ac => ac.uId === c?.configuredChannelUId);
      if (!!channel && !channelUIds.includes(channel.channelUId)) {
        channelUIds.push(channel.channelUId);
      }
    });
    return channelUIds;
  }

  public getUniqueConfiguredChannels(settings: ChannelsCascadeSettings): ConfiguredChannel[] {
    const configuredChannels = [];

    settings?.channelNotifications?.forEach(c => {
      const configuRedChannel = this.getConfiguredChannel(c.configuredChannelUId);
      if (!!configuRedChannel && !configuredChannels.find(ch => ch.uId === c)) {
        configuredChannels.push(configuRedChannel);
      }
    });

    return configuredChannels;
  }

  public getUniqueChannelTypes(settings: ChannelsCascadeSettings): ChannelType[] {
    const channelTypes = [];

    settings?.channelNotifications?.forEach(c => {
      const channel = this.availableChannels?.find(ac => ac.uId === c.configuredChannelUId);
      if (!!channel && !channelTypes.includes(channel.channelType)) {
        channelTypes.push(channel.channelType);
      }
    });

    return channelTypes;
  }

  public getConfiguredChannel(uId: string): ConfiguredChannel {
    return this.availableChannels?.find(c => c.uId === uId);
  }

  public getChannelSettingsDisplay(settings: ChannelNotificationSettings): string {
    let display =
      (settings.startTimeMinutes == 0
        ? this.translateService.instant('COMMUNICATIONS.START')
        : this.translateService.instant('COMMUNICATIONS.START-AFTER')) +
      ' ' +
      this.startTimes.find(s => s.value === settings.startTimeMinutes)?.name +
      '. ';
    if (settings.resendTimeMinutes > 0) {
      display += this.translateService.instant('COMMUNICATIONS.RESEND-EVERY') + ' ';
    }
    display += this.resendTimes.find(s => s.value === settings.resendTimeMinutes)?.name + '. ';

    if (settings.resendTimeMinutes > 0) {
      display +=
        this.translateService.instant('COMMUNICATIONS.STOP-AFTER') +
        ' ' +
        this.stopTimes.find(s => s.value === settings.stopTimeMinutes)?.name +
        '.';
    }

    return display;
  }

  getChannelIcon(channelUId: string, isDirectMessage: boolean = true): string {
    if (channelUId === ChannelUIds.TopoEmailChannelUId || channelUId === ChannelUIds.SendGridEmailChannelUId) {
      return 'icon icon-email';
    } else if (channelUId === ChannelUIds.SlackChannelUId) {
      return 'icon icon-logo_slack' + (isDirectMessage ? '' : ' channel');
    } else if (channelUId === ChannelUIds.MicrosoftTeamsChannelUId) {
      return 'icon icon-logo_ms_teams' + (isDirectMessage ? '' : ' channel');
    } else if (channelUId === ChannelUIds.TwilioChannelUId || channelUId === ChannelUIds.HorizonSmsChannelUId) {
      return 'icon icon-text_chat';
    } else if (channelUId === ChannelUIds.TwilioTtsChannelUId) {
      return 'icon icon-phone_1';
    } else if (channelUId === ChannelUIds.WhatsAppChannelUId) {
      return 'icon icon-whatsapp';
    } else if (channelUId === ChannelUIds.HorizonMobilePushChannelUId) {
      return 'icon icon-small_solid_mobile_phone_check';
    } else if (channelUId === ChannelUIds.GwtsMobileChannelUId) {
      return 'icon icon-security';
    } else return 'icon icon-satellite-dish';
  }

  getChannelName(channelUId: string): string {
    if (channelUId === ChannelUIds.TopoEmailChannelUId || channelUId === ChannelUIds.SendGridEmailChannelUId) {
      return this.translateService.instant('COMMUNICATIONS.TAB-EMAIL-TYPE');
    } else if (channelUId === ChannelUIds.SlackChannelUId) {
      return this.translateService.instant('COMMUNICATIONS.TAB-SLACK-TYPE');
    } else if (channelUId === ChannelUIds.MicrosoftTeamsChannelUId) {
      return this.translateService.instant('COMMUNICATIONS.TAB-TEAMS-TYPE');
    } else if (channelUId === ChannelUIds.TwilioChannelUId || channelUId === ChannelUIds.HorizonSmsChannelUId) {
      return this.translateService.instant('COMMUNICATIONS.TAB-SMS-TYPE');
    } else if (channelUId === ChannelUIds.TwilioTtsChannelUId) {
      return this.translateService.instant('COMMUNICATIONS.TAB-TTS-TYPE');
    } else if (channelUId === ChannelUIds.WhatsAppChannelUId) {
      return this.translateService.instant('COMMUNICATIONS.TAB-WHATSAPP-TYPE');
    } else if (channelUId === ChannelUIds.HorizonMobilePushChannelUId) {
      return this.translateService.instant('COMMUNICATIONS.TAB-PUSH-TYPE');
    } else if (channelUId === ChannelUIds.GwtsMobileChannelUId) {
      return this.translateService.instant('COMMUNICATIONS.TAB-GWTS');
    } else return this.translateService.instant('COMMUNICATIONS.TAB-OTHER-CHANNELS-TYPES');
  }

  getChannelUIdsForTemplates(channelUIds: string[]): string[] {
    const availableChannelUIds: string[] = [];
    channelUIds.forEach(channelUId => {
      if (channelUId === ChannelUIds.TopoEmailChannelUId || channelUId === ChannelUIds.SendGridEmailChannelUId) {
        availableChannelUIds.push(ChannelUIds.TopoEmailChannelUId);
      } else if (channelUId === ChannelUIds.SlackChannelUId) {
        availableChannelUIds.push(ChannelUIds.SlackChannelUId);
      } else if (channelUId === ChannelUIds.MicrosoftTeamsChannelUId) {
        availableChannelUIds.push(ChannelUIds.MicrosoftTeamsChannelUId);
      } else if (channelUId === ChannelUIds.TwilioChannelUId || channelUId === ChannelUIds.HorizonSmsChannelUId) {
        availableChannelUIds.push(ChannelUIds.TwilioChannelUId);
      } else if (channelUId === ChannelUIds.TwilioTtsChannelUId) {
        availableChannelUIds.push(ChannelUIds.TwilioTtsChannelUId);
      } else if (channelUId === ChannelUIds.WhatsAppChannelUId) {
        availableChannelUIds.push(ChannelUIds.WhatsAppChannelUId);
      } else if (channelUId === ChannelUIds.HorizonMobilePushChannelUId) {
        availableChannelUIds.push(ChannelUIds.HorizonMobilePushChannelUId);
      } else if (channelUId === ChannelUIds.GwtsMobileChannelUId) {
        availableChannelUIds.push(ChannelUIds.GwtsMobileChannelUId);
      }
    });

    return availableChannelUIds;
  }

  getFullChannelName(channelUId: string): string {
    switch (channelUId) {
      case ChannelUIds.TopoEmailChannelUId:
        return this.translateService.instant('NOTIFICATIONS.CHANNEL-TOPO-EMAIl-NAME');
      case ChannelUIds.SendGridEmailChannelUId:
        return this.translateService.instant('NOTIFICATIONS.CHANNEL-SENDGRID-NAME');
      case ChannelUIds.SlackChannelUId:
        return this.translateService.instant('NOTIFICATIONS.CHANNEL-SLACK-NAME');
      case ChannelUIds.MicrosoftTeamsChannelUId:
        return this.translateService.instant('NOTIFICATIONS.CHANNEL-MS-TEAMS-NAME');
      case ChannelUIds.TwilioChannelUId:
        return this.translateService.instant('NOTIFICATIONS.CHANNEL-TWILIO-NAME');
      case ChannelUIds.HorizonSmsChannelUId:
        return this.translateService.instant('NOTIFICATIONS.CHANNEL-HORIZON-SMS-NAME');
      case ChannelUIds.TwilioTtsChannelUId:
        return this.translateService.instant('NOTIFICATIONS.CHANNEL-TWILIO-TTS-NAME');
      case ChannelUIds.WhatsAppChannelUId:
        return this.translateService.instant('NOTIFICATIONS.CHANNEL-WHATSAPP-NAME');
      case ChannelUIds.HorizonMobilePushChannelUId:
        return this.translateService.instant('NOTIFICATIONS.CHANNEL-HORIZON-MOBILE-PUSH-NAME');
      case ChannelUIds.GwtsMobileChannelUId:
        return this.translateService.instant('NOTIFICATIONS.CHANNEL-GWTS-MOBILE-NAME');
    }
    return '';
  }

  private getConfiguredChannels(): Observable<ConfiguredChannel[]> {
    const userContext = this.userContext.getUserContext();

    if (!userContext.hasPermission(Permissions.ChannelConfigRead)) {
      return of([]);
    }

    const params: QueryParams = {
      skip: 0,
      take: 1000,
      sortField: 'Name',
      sortDirection: 'Ascending'
    };
    return this.communicationsApiService.getConfiguredChannels(params).pipe(
      catchError(() => of({ data: [] })),
      map(value => {
        return value?.data;
      })
    );
  }

  public getSingleRecipientChannel(recipient: MassNotificationRecipient): ConfiguredChannel {
    let singleRecipientChannel = null;
    if (recipient.recipientAddresses?.length > 0) {
      const recipientChannel = this.getConfiguredChannel(recipient.recipientAddresses[0]?.configuredChannelUId);
      if (
        recipientChannel?.channelUId === ChannelUIds.MicrosoftTeamsChannelUId ||
        recipientChannel?.channelUId === ChannelUIds.SlackChannelUId
      ) {
        if (recipientChannel?.settings?.sendDirectMessages !== true) {
          singleRecipientChannel = recipientChannel;
        }
      }
    }

    return singleRecipientChannel;
  }

  ngOnDestroy() {
    this.channelsLoadedEvents$.complete();
    this.channelsLoadedEvents$ = null;
  }
}
